import { AxiosRequestConfig, AxiosResponse } from "axios";
import omit from "lodash/omit";

import { Moment } from "moment";

import {
  buildExportFileName,
  type ExportFileNameUser,
} from "@/v1/utils/exportFileName";
import type { CreateUserRequestDto } from "@/v1/types/CreateUser.type";
import type {
  EditableAccountAttributes,
  IAccount,
} from "@/v1/types/IAccount.type";
import {
  Document,
  DepreciationTransactionRow,
  Euer,
  ExternalResource,
  FetchTransactionsResponse,
  IBusinessAsset,
  ICreateBusinessAssetFromTransactionPayload,
  ICreateBusinessAssetPayload,
  IExitBusinessAssetPayload,
  IRule,
  ITaxNumber,
  ITransaction,
  IUpdateBusinessAssetPayload,
  IUser,
  IVatDeclarationUser,
  AccountOption,
  KontaxPlan,
  KontaxTransactionFilterOptions,
  KontaxTransactionMeta,
  KontaxUserStatus,
  KontaxUserStatusAdditionalParams,
  NextVatDeclarationStatus,
  PaymentFrequency,
  PermanentExtensionStatus,
  TransactionSplit,
  UserDependent,
  UstvaPeriod,
  VatMeta,
  VatSubmissionEntry,
  ZmDeclarationEntry,
  ZmUserStatus,
} from "../../types";
import { parseZmDeclarationsResponse } from "../../utils/parseZmDeclarationsResponse";
import BaseApi from "./BaseApi";

export interface ZmTransactionUser {
  email: string;
  firstName?: string;
  lastName?: string;
  legalName?: string;
  frequency: PaymentFrequency;
  status: keyof typeof ZmUserStatus;
}

export interface UsersWithZmTransactionsResponse {
  page: number;
  users: ZmTransactionUser[];
}

/**
 * Fields extracted by the synchronous OCR endpoint from an uploaded receipt.
 * `amount` is in cents and non-negative; `date` is an ISO date string. Any
 * field the OCR could not read is omitted.
 */
export interface OcrReceiptFields {
  amount?: number;
  date?: string;
  name?: string;
  documentNumber?: string;
}

class KontaxApi extends BaseApi {
  protected get moduleUrl(): string {
    return "/kontax";
  }

  public getUsers = async (filterOptions: {
    search?: string;
    emailLike?: string;
    page?: number;
    pageSize?: number;
    filters?: Record<string, any>;
    sortField?: string;
    sortOrder?: string;
    assigneeId?: string | "unassigned";
    firm?: "smarta" | "preis-und-partner";
  }): Promise<{ users: Array<IUser>; count: number }> => {
    const { filters, ...rest } = filterOptions;

    // The backend `/users` handler reads `filters` with `JSON.parse`, so it must
    // be sent as a JSON string. Axios' default param serializer would otherwise
    // encode the object as bracket notation (`filters[user.kontaxStatus][0]=...`),
    // which `JSON.parse` rejects — silently dropping the filter (SMT-1247).
    const params = {
      ...rest,
      ...(filters && Object.keys(filters).length > 0
        ? { filters: JSON.stringify(filters) }
        : {}),
    };

    const { data } = await this.get("/users", { params });

    return {
      users: data.users,
      count: data.count,
    };
  };

  // Customer→KAM assignment (assign / take-over / release) and the operator
  // list/CRUD now go through the generated smarta-accounts client in
  // `modules/integrations/accounts`, the same way banking talks to accounts.
  // The bespoke assignMandant* and operator methods were removed.

  public getUserByEmail = async (email: string): Promise<IUser> => {
    const { users } = await this.getUsers({ search: email });
    return users[0];
  };

  public getAccountIdByEmail = async (email: string): Promise<string> => {
    const { users } = await this.getUsers({ search: email });
    return `${users[0].accountId}`;
  };

  public getUsersWithDeclarations = async (filterOptions: {
    email?: string;
    page?: number;
    status?: NextVatDeclarationStatus;
    plans?: Array<KontaxPlan>;
    vatPaymentFrequency?: PaymentFrequency;
    assigneeId?: string | "unassigned";
  }): Promise<{
    declarations: Array<IVatDeclarationUser>;
    page: number;
  }> => {
    const { email, page, status, vatPaymentFrequency, plans, assigneeId } =
      filterOptions;

    const params = email
      ? { email }
      : {
          ...(status && { status }),
          ...(page && { page }),
          ...(plans && { plan: plans.join(",") }),
          ...(vatPaymentFrequency && { vatPaymentFrequency }),
          ...(assigneeId !== undefined && { assigneeId }),
        };

    const { data } = await this.get("/users/declarations", { params });

    return data;
  };

  public getUsersWithZmTransactions = async (filterOptions: {
    dateFrom: string;
    dateTo: string;
    page?: number;
    frequency?: PaymentFrequency;
    email?: string;
    status?: keyof typeof ZmUserStatus;
  }): Promise<UsersWithZmTransactionsResponse> => {
    const { data } = await this.get("/zm/users", {
      params: filterOptions,
    });

    return data;
  };

  public getZmUserDeclarations = async (
    email: string
  ): Promise<ZmDeclarationEntry[]> => {
    const { data } = await this.get(
      `/zm/users/${encodeURIComponent(email)}/declarations`,
      {
        skipNotification: true,
      }
    );

    return parseZmDeclarationsResponse(data);
  };

  public getZmDeclarationPdf = async (
    email: string,
    id: string
  ): Promise<string> => {
    return this.getDeclarationPdf(email, id);
  };

  public updateUser = async (user: {
    email: string;
    taxNumber?: ITaxNumber;
    businessTypeComment?: string;
    businessStartDate?: string;
    businessEndDate?: string;
    permanentExtensionStatus?: PermanentExtensionStatus;
  }): Promise<IUser> => {
    const { data } = await this.patch("/users", user);
    return data;
  };

  public updateUserStatus = async (
    email: string,
    status: KontaxUserStatus | null,
    additionalParams: KontaxUserStatusAdditionalParams = {}
  ) => {
    const { data } = await this.put(`/users/${email}/status`, {
      status,
      ...additionalParams,
    });

    return data;
  };

  public updateAccount = async (
    id: number,
    payload: EditableAccountAttributes
  ): Promise<IAccount> => {
    const { data } = await this.patch(`/accounts/${id}`, payload);
    return data;
  };

  public updateNextDeclarationData = async (
    {
      email,
      nextDeclarationDuePeriod,
      nextDeclarationDueYear,
      nextDeclarationStatus,
      vatLastCheckedAt,
    }: {
      email: string;
      nextDeclarationDuePeriod?: number;
      nextDeclarationDueYear?: number;
      nextDeclarationStatus?: NextVatDeclarationStatus;
      vatLastCheckedAt?: string;
    },
    config?: AxiosRequestConfig
  ) => {
    return this.put(
      `/users/${email}/declarations`,
      {
        nextDeclarationDuePeriod,
        nextDeclarationDueYear,
        nextDeclarationStatus,
        vatLastCheckedAt,
      },
      config
    );
  };

  public getVatMeta = async (email: string): Promise<VatMeta> => {
    const { data } = await this.get(`/users/${email}/vat-declaration-meta`);
    return data;
  };

  /*
   * The customer as far as an export file name needs it. Falls back to the
   * email alone, so a failed lookup never blocks the download (SMT-1454).
   */
  public getExportCustomer = async (
    email: string
  ): Promise<ExportFileNameUser> => {
    try {
      const { user } = await this.getVatMeta(email);
      return user ?? { email };
    } catch {
      return { email };
    }
  };

  public getDfvData = async (email: string) => {
    const { user } = await this.getVatMeta(email);
    if (!user) throw new Error("User not found.");
    const { data } = await this.get(`/private/admin/user/${user.id}/dfv`);
    // The customer travels along so exports can be named after them (SMT-1454).
    return { ...data, user };
  };

  public submitDfv = async (email: string, estimate?: number) => {
    const { user } = await this.getVatMeta(email);
    if (!user) throw new Error("User not found.");
    const { data } = await this.post(`/private/admin/user/${user.id}/dfv`, {
      year: new Date().getFullYear(),
      estimate,
    });
    return data as {
      id: number;
      pdf?: string;
      customerName?: string;
      year?: number;
      pdfs?: Array<{ type: "dfv" | "svz"; id: number; pdf?: string }>;
    };
  };

  public submitSvz = async (email: string, estimate?: number) => {
    const { user } = await this.getVatMeta(email);
    if (!user) throw new Error("User not found.");
    const { data } = await this.post(`/private/admin/user/${user.id}/svz`, {
      year: new Date().getFullYear(),
      estimate,
    });
    return data as {
      id: number;
      pdf?: string;
      customerName?: string;
      year?: number;
      pdfs?: Array<{ type: "dfv" | "svz"; id: number; pdf?: string }>;
    };
  };

  public getDfvPdf = async (email: string, id: number): Promise<string> => {
    const { user } = await this.getVatMeta(email);
    if (!user) throw new Error("User not found.");
    const { data } = await this.get(
      `/private/admin/user/${user.id}/dfv/${id}/pdf`
    );
    return data.url;
  };

  public getVatSubmissionList = async (
    email: string
  ): Promise<VatSubmissionEntry[]> => {
    const { data } = await this.get("/users/vat-submissions", {
      params: { email },
      skipNotification: true,
    });
    return data;
  };

  public getDeclarationPdf = async (
    email: string,
    id: string
  ): Promise<string> => {
    const { data } = await this.get(`/users/${email}/declarations/${id}`);
    return data;
  };

  public getExternalUrl = async (
    email: string,
    resource: ExternalResource
  ): Promise<string> => {
    const { data } = await this.get(`/users/${email}/external-url/${resource}`);
    return data;
  };

  public fetchTransactions = async (
    filters?: KontaxTransactionFilterOptions,
    limit?: number,
    pagination?: { page?: number; pageSize?: number }
  ): Promise<FetchTransactionsResponse> => {
    const params: any = {
      ...omit(filters, "plans"),
      plan: filters?.plans,
      ...(limit && { limit }),
    };

    if (pagination?.page !== undefined) {
      params.page = pagination.page;
    }

    if (pagination?.pageSize !== undefined) {
      params.pageSize = pagination.pageSize;
    }

    const { data } = await this.get("/transactions", { params });

    // This code is here to make this endpoint backwards compatible
    // Please remove once this endpoint only sends an array of transactions
    let transactions: ITransaction[] = [];
    if (data?.transactions) {
      transactions.push(...data.transactions);
    }

    if (data?.transaction) {
      transactions.push(data.transaction);
    }

    return {
      count: data?.count,
      transactions,
    };
  };

  public fetchTransactionCount = async (
    filters?: KontaxTransactionFilterOptions
  ): Promise<number> => {
    const params: any = {
      ...omit(filters, "plans"),
      plan: filters?.plans,
    };

    const { data } = await this.get("/transactions/count", { params });
    return data?.count;
  };

  public fetchTransactionCurrencies = async (
    filters?: KontaxTransactionFilterOptions
  ): Promise<string[]> => {
    const params: any = {
      ...omit(filters, "plans"),
      plan: filters?.plans,
    };

    const { data } = await this.get("/transactions/currencies", { params });
    return data?.currencies ?? [];
  };

  public fetchTransactionAccounts = async (
    filters?: KontaxTransactionFilterOptions
  ): Promise<AccountOption[]> => {
    const params: any = {
      ...omit(filters, "plans"),
      plan: filters?.plans,
    };

    const { data } = await this.get("/transactions/accounts", { params });
    return data?.accounts ?? [];
  };

  public fetchTransactionById = async (
    id: string
  ): Promise<ITransaction | null> => {
    const { data } = await this.get(`/transactions/${id}`);
    return data;
  };

  public getUstvaElster = async (
    accountId: string,
    year: number,
    period: UstvaPeriod,
    contentType: string
  ): Promise<string> => {
    const response = await this.get(
      `/accounts/${accountId}/ustva/${year}/${period}`,
      {
        headers: {
          Accept: contentType,
        },
      }
    );
    return response.data;
  };

  public getEuerElster = async (
    accountId: string,
    startDate: string,
    endDate: string
  ): Promise<Euer> => {
    const response = await this.get(
      `/accounts/${accountId}/euer/${startDate}/${endDate}`,
      {
        headers: {
          Accept: "application/json",
        },
      }
    );
    return response.data;
  };

  public getZmElster = async (
    email: string,
    year: number,
    period: string,
    contentType: string
  ): Promise<any> => {
    const response = await this.get(
      `/zm/users/${encodeURIComponent(email)}/elster/${year}/${period}`,
      {
        headers: {
          Accept: contentType,
        },
      }
    );
    return response.data;
  };

  public getZmElsterStatus = async (
    email: string,
    year: number,
    period: string
  ): Promise<any> => {
    const response = await this.get(
      `/zm/users/${encodeURIComponent(email)}/elster/${year}/${period}/status`
    );
    return response.data;
  };

  public sendZmViaEric = async (
    email: string,
    year: number,
    period: string
  ): Promise<any> => {
    const response = await this.post(
      `/zm/users/${encodeURIComponent(email)}/elster/${year}/${period}`,
      {},
      { responseType: "arraybuffer" }
    );
    return response.data;
  };

  public getAutocategorizationRules = async (): Promise<{
    rules: Array<IRule>;
  }> => {
    const { data } = await this.get("/autocategorization-rules");

    return {
      rules: data,
    };
  };

  public updateAutocategorizationRule = async (rule: {
    id: number;
    enabled?: boolean;
  }) => {
    return this.patch("/autocategorization-rules", rule);
  };

  public getUstvaStatus = async (
    accountId: string,
    period: string,
    year: number
  ) => {
    const response = await this.get(
      `/accounts/${accountId}/ustva/${year}/${period}/status`
    );
    return response.data;
  };

  public sendViaEric = async (
    accountId: string,
    period: string,
    year: number
  ) => {
    const response = await this.post(
      `/accounts/${accountId}/ustva/${year}/${period}`,
      {},
      { responseType: "arraybuffer" }
    );
    return response.data;
  };

  public createTransactionAsset = async (
    transactionId: string,
    name: string,
    filetype: string
  ) => {
    const response = await this.post(`/transactions/${transactionId}/assets`, {
      name,
      filetype,
    });
    return response.data;
  };

  public acknowledgeTransactionAsset = async (
    transactionId: string,
    assetId: string
  ) => {
    return this.patch(`/transactions/${transactionId}/assets/${assetId}`);
  };

  /**
   * Synchronously OCRs an uploaded receipt and returns the extracted fields,
   * without persisting anything. Used to pre-fill the "+ Add transaction" form
   * in the Verification View.
   */
  public ocrTransactionReceipt = async (
    email: string,
    file: File
  ): Promise<OcrReceiptFields> => {
    const form = new FormData();
    form.append("file", file);

    const response = await this.post<OcrReceiptFields>(
      `/users/${email}/transactions/ocr`,
      form,
      { headers: { "Content-Type": "multipart/form-data" } }
    );

    return response.data;
  };

  public createTransactionSplit = async (
    transactionId: string,
    splits: Array<TransactionSplit>
  ) => this.post(`/transactions/${transactionId}/splits`, { splits });

  public updateTransactionSplit = async (
    transactionId: string,
    splits: Array<TransactionSplit>
  ) =>
    this.patch(`/transactions/${transactionId}/splits`, {
      splits: splits.map((s) => omit(s, ["category"])),
    });

  public deleteTransactionSplit = async (transactionId: string) =>
    this.delete(`/transactions/${transactionId}/splits`);

  public updateDependents = async (
    email: string,
    userDependents: UserDependent[]
  ) => {
    const { data } = await this.put(`/users/${email}/dependents`, {
      dependents: userDependents,
    });
    return data;
  };

  public createBusinessAsset = async ({
    email,
    businessAsset,
  }: {
    email: string;
    businessAsset: ICreateBusinessAssetPayload;
  }) =>
    this.post(`/users/${email}/business-assets`, {
      businessAsset,
    });

  public createBusinessAssetFromTransaction = async ({
    transactionId,
    businessAsset,
    transactionMeta,
  }: {
    transactionId: string;
    businessAsset: ICreateBusinessAssetFromTransactionPayload;
    transactionMeta: KontaxTransactionMeta;
  }) =>
    this.post(`/transactions/${transactionId}/business-assets`, {
      businessAsset,
      transactionMeta,
    });

  public updateBusinessAsset = async ({
    id,
    ...info
  }:
    | IUpdateBusinessAssetPayload
    | IExitBusinessAssetPayload): Promise<IBusinessAsset> => {
    const { data } = await this.patch(`/business-assets/${id}`, {
      ...info,
    });
    return data;
  };

  public fetchDepreciationTransactionRows = async ({
    email,
    year,
  }: {
    email: string;
    year: number;
  }): Promise<DepreciationTransactionRow[]> => {
    const { data } = await this.get(
      `/users/${encodeURIComponent(
        email
      )}/business-assets/${year}/depreciation-rows`
    );
    return data.rows;
  };

  public acknowledgeAssetsUploaded = async (assetIds: string[]) => {
    return this.post(`/assets/acknowledge-assets-uploaded`, { assetIds });
  };

  // Operator list + CRUD moved to the generated smarta-accounts client in
  // `modules/integrations/accounts` (listOperators / createOperator /
  // updateOperator / deleteOperator), so the operator surface is consumed the
  // same way banking consumes its generated client.

  public uploadDocument = async (
    email: string,
    file: File,
    documentCategoryId?: string | null,
    year?: number | null
  ): Promise<Document> => {
    const form = new FormData();
    form.append("file", file);

    if (documentCategoryId) {
      form.append("documentCategoryId", documentCategoryId);
    }

    if (year) {
      form.append("year", year.toString());
    }

    return (
      await this.post(`/users/${email}/documents`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      })
    ).data;
  };

  /*
   * The backend names the ZIP via Content-Disposition (`<Customer> - Dokumente
   * [- <year>].zip`); the same name is built here as the fallback (SMT-1454).
   */
  public downloadDocumentsArchive = async (
    user: ExportFileNameUser & { email: string },
    ids: string[],
    year?: number
  ) =>
    await this.download(
      `/users/${user.email}/documents/archive`,
      buildExportFileName({
        user,
        exportType: "Dokumente",
        period: year ? { kind: "year", year } : undefined,
        extension: "zip",
      }),
      {
        params: { ids, year },
        responseType: "blob",
      }
    );

  public uploadReceipt = async (
    email: string,
    file: File
  ): Promise<Document> => {
    const form = new FormData();
    form.append("file", file);
    return (
      await this.post(`/users/${email}/receipts`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      })
    ).data;
  };

  public getBWAPdf = async (
    email: string,
    fromDate: string,
    toDate: string
  ) => {
    return await this.get(
      `/users/${email}/bwa-pdf/?fromDate=${fromDate}&toDate=${toDate}`,
      { responseType: "blob" }
    );
  };

  public getDocuwareDocument = async (id: string) => {
    return this.get(`/docuware/document/${id}`, {
      responseType: "arraybuffer",
    });
  };

  public deleteEndlessDatevExports = async (
    userId: number
  ): Promise<AxiosResponse<{ success: boolean }>> => {
    return this.post(
      `/private/admin/user/${userId}/delete-endless-datev-exports`
    );
  };

  public exportTransactions = async (
    userId: number,
    from: string,
    to: string
  ): Promise<AxiosResponse<{ csv: string; success?: boolean }>> => {
    return this.get(`/private/admin/user/${userId}/transactions`, {
      params: { from, to },
    });
  };

  public exportSmartaBankingTransactions = async (
    userId: number,
    from?: string,
    to?: string
  ): Promise<AxiosResponse<{ csv: string; success?: boolean }>> => {
    return this.get(
      `/private/admin/user/${userId}/export-smarta-banking-transactions`,
      {
        params: { from, to },
      }
    );
  };

  public resetDeclarationApproval = async (
    userId: number,
    date: Moment,
    declarationType: string
  ): Promise<AxiosResponse<{ success: boolean }>> => {
    return this.post(
      `/private/admin/user/${userId}/reset-declaration-approval`,
      {
        year: date.year(),
        declarationType,
      }
    );
  };

  public createDatevExport = async (
    userId: number,
    body: { skr: "SKR03" | "SKR04"; date: Moment; withReceipts: boolean }
  ): Promise<AxiosResponse<{ id?: string }>> => {
    return this.post(`/private/admin/user/${userId}/datev-exports`, {
      skr: body.skr,
      year: body.date.year(),
      withReceipts: body.withReceipts,
    });
  };

  public getDatevExport = async (
    userId: number,
    id: string
  ): Promise<AxiosResponse<{ id: string; url: string | null }>> => {
    return this.get(`/private/admin/user/${userId}/datev-exports/${id}`);
  };

  public exportEURTransactions = async (
    userId: number,
    date: Moment,
    filetype: string,
    elsterField: string
  ): Promise<AxiosResponse> => {
    return this.post(
      `/private/admin/user/${userId}/export-euer-transactions`,
      {
        year: date.year(),
        filetype,
        field: elsterField,
      },
      {
        responseType: "blob",
      }
    );
  };

  // Named distinctly from `exportTransactions` above (a separate, pre-existing
  // from/to CSV export used by CommandHubView) since this exports the current
  // verification-view filter set as CSV or PDF (SMT-1225).
  public exportFilteredTransactions = async (
    userId: number,
    filters: KontaxTransactionFilterOptions,
    filetype: "csv" | "pdf",
    language: "de" | "en"
  ): Promise<AxiosResponse> => {
    return this.post(
      `/private/admin/user/${userId}/export-transactions`,
      { filters, filetype, language },
      {
        responseType: "blob",
      }
    );
  };

  public exportSusa = async (
    userId: number,
    year: number,
    fromMonth: number,
    toMonth: number
  ): Promise<AxiosResponse> => {
    return this.post(
      `/private/admin/user/${userId}/susa-pdf`,
      {
        year,
        fromMonth,
        toMonth,
      },
      {
        responseType: "blob",
      }
    );
  };

  public resetUserTaxCase = async (
    userId: number,
    date: Moment
  ): Promise<AxiosResponse> => {
    return this.post(`/private/admin/user/${userId}/reset-tax-case`, {
      year: date.year(),
    });
  };

  public resetSkippedIncomeTax = async (
    userId: number,
    date: Moment
  ): Promise<AxiosResponse> => {
    return this.post(`/private/admin/user/${userId}/reset-skipped-income-tax`, {
      year: date.year(),
    });
  };

  public createUser = async (payload: CreateUserRequestDto): Promise<IUser> => {
    const { data } = await this.post("/user", payload);
    return data;
  };

  public generateInviteLink = async (
    userId: number
  ): Promise<AxiosResponse<{ inviteLink: string; expiresAt: string }>> => {
    return this.post(`/private/admin/user/${userId}/generate-invite-link`);
  };
}

const kontaxApi = new KontaxApi();

export default kontaxApi;
